import { Controller } from "@hotwired/stimulus"
import { createUserChannel } from "../channels/user_channel"
import { showMessageToast } from "../utils/message_toast"

export default class extends Controller {
  static values = { currentUserId: Number, organizationId: Number, cursor: Number }

  connect() {
    this.active = true
    this.storageKey = `notifications:${this.currentUserIdValue}:${this.organizationIdValue}`
    const stored = this.readState()
    this.cursor = stored?.cursor ?? this.cursorValue
    this.seen = new Set(stored?.seen || [])
    this.pending = (stored?.pending || []).filter((entry) => entry.expiresAt > Date.now())
    // Save the page's baseline before a fast import can navigate away from it.
    this.saveState()
    this.pending.forEach((entry) => this.showNotification(entry))
    this.subscription = createUserChannel(
      this.currentUserIdValue,
      (data) => this.received(data),
      () => {
        if (!this.active) return
        this.syncing = false
        this.sync()
      }
    )
  }

  disconnect() {
    this.active = false
    this.subscription?.unsubscribe()
  }

  sync() {
    if (this.syncing) {
      this.syncAgain = true
      return
    }
    this.syncing = !!this.subscription?.perform("sync", { after_id: this.cursor })
  }

  received(data) {
    if (data.event === "message.created") {
      this.notify(data)
      this.sync()
    } else if (data.event === "channel.read") {
      this.sync()
    } else if (data.event === "messages.synced") {
      data.messages.forEach((message) => this.notify(message))
      this.cursor = Math.max(this.cursor, data.cursor)
      this.saveState()
      this.updateUnreadCounts(data.unread_counts)
      this.syncing = false
      if (data.has_more || this.syncAgain) {
        this.syncAgain = false
        this.sync()
      }
    }
  }

  notify(data) {
    const message = data.message
    if (message.id <= this.cursor || this.seen.has(message.id)) return
    this.seen.add(message.id)
    this.saveState()
    if (message.read) return

    const panel = document.getElementById("messagesOffcanvas")
    const viewing = !document.hidden && panel?.open &&
      Number(panel.dataset.communicationMessagesChannelIdValue) === data.channel_id &&
      panel.querySelector(".chat-panel.active")
    if (!viewing || message.message_type === "system") {
      const entry = { data, expiresAt: Date.now() + 8000 }
      this.pending.push(entry)
      this.saveState()
      this.showNotification(entry)
    }
  }

  showNotification(entry) {
    const data = entry.data
    const id = data.message.id
    if (this.element.querySelector(`[data-notification-id="${id}"]`)) return

    const toast = showMessageToast(data.channel_name || "New message", data.message.content, data.message.file, entry.expiresAt - Date.now())
    if (!toast) return
    toast.dataset.notificationId = id
    toast.addEventListener("hidden.bs.toast", () => {
      this.pending = this.pending.filter((item) => item.data.message.id !== id)
      this.saveState()
    }, { once: true })
  }

  updateUnreadCounts(counts) {
    const badge = document.getElementById("global_message_count")
    const count = Object.values(counts).reduce((total, value) => total + value, 0)
    if (badge) {
      badge.textContent = count
      badge.classList.toggle("d-none", count === 0)
      badge.previousElementSibling?.classList.toggle("fa-beat", count > 0)
    }
    document.dispatchEvent(new CustomEvent("notifications:sync", { detail: counts }))
  }

  readState() {
    try {
      const state = JSON.parse(sessionStorage.getItem(this.storageKey))
      if (Number.isSafeInteger(state?.cursor) && state.cursor >= 0 && Array.isArray(state.seen) && (!state.pending || Array.isArray(state.pending))) return state
    } catch (_error) {
      // Live delivery still works if browser storage is unavailable.
    }
    return null
  }

  saveState() {
    // Keep deduplication bounded and scoped to this tab, user, and organization.
    this.seen = new Set([...this.seen].slice(-200))
    this.pending = this.pending.filter((entry) => entry.expiresAt > Date.now()).slice(-10)
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ cursor: this.cursor, seen: [...this.seen], pending: this.pending }))
    } catch (_error) {
      // Do not let storage restrictions stop a notification from appearing.
    }
  }
}
